'use client';

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { Card } from './card';
import { Numeric } from './numeric';
import { Button } from './button';
import { Chip } from './Chip';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Input } from './input';
import { RangeSlider } from './RangeSlider';
import { Select } from './select';

export interface FilterOption {
  value: string;
  label: string;
  /**
   * Optional decoration (e.g. a venue colour dot) shown on the option's chip in the panel and on
   * its applied chip. A native dropdown `<option>` cannot render it, so the single-dropdown form
   * and long (dropdown) groups show the plain label only; the name must never depend on it.
   */
  adornment?: ReactNode;
  /** Caller-owned result count, including zero. */
  count?: number;
}

/** Pick-one list of options. */
export interface SelectFilterGroup {
  kind: 'select';
  id: string;
  label: string;
  /** The "no filter" option of the dropdown form, e.g. `All divisions`. */
  allLabel: string;
  allCount?: number;
  /** Keep a meaningful subset (e.g. Following) visible alongside All even with one option. */
  showWhenSingle?: boolean;
  options: FilterOption[];
}

/** Min/max within the group's bounds, plus the optional toggle's state. Serialisable. */
export interface RangeValue {
  min: number;
  max: number;
  /** State of the group's `toggle`; absent = the toggle's default. */
  toggle?: boolean;
}

/**
 * A min/max within bounds. Applied only when it differs from the bounds (or its toggle from the
 * toggle's default). Always behind the Filter button: a range cannot be a single dropdown.
 */
export interface RangeFilterGroup {
  kind: 'range';
  id: string;
  label: string;
  /** Bounds; a group with `min >= max` is hidden. */
  min: number;
  max: number;
  step?: number;
  /** Accessible names of the two number inputs, e.g. `Rating (min)`. */
  minLabel: string;
  maxLabel: string;
  /** Applied chip text; default `{label} {min}–{max}`. */
  formatChip?: (value: { min: number; max: number }) => string;
  /** Optional checkbox stored beside the range (e.g. "Include unrated"). */
  toggle?: {
    label: string;
    default: boolean;
    /**
     * Short applied-chip text when ONLY the toggle differs from its default (the range is at its
     * bounds), e.g. `Including unknown age`. Without it the chip falls back to the range text.
     */
    chipLabel?: (on: boolean) => string;
    /**
     * Even shorter text appended to the range when both moved, e.g. `Age 30\u201345 \u00b7 incl. unknown`.
     * Defaults to `chipLabel`.
     */
    chipSuffix?: (on: boolean) => string;
  };
}

/**
 * Pick-any list of options: each option's chip toggles it. Always behind the Filter button (a
 * native dropdown cannot multi-select); every option is a chip in the panel, however many. Each
 * chosen option is its own applied chip; removing one drops that option only.
 */
export interface MultiFilterGroup {
  kind: 'multi';
  id: string;
  label: string;
  options: FilterOption[];
}

export type FilterGroup = SelectFilterGroup | RangeFilterGroup | MultiFilterGroup;

/**
 * Per group id: the chosen option value (select group), a {@link RangeValue} (range group) or the
 * chosen option values (multi group, in option order). undefined (or absent) = no filter; a multi
 * group never holds an empty list. Use {@link selectValue} / {@link rangeValue} /
 * {@link multiValue} to read one.
 */
export type FilterValue = Record<string, string | RangeValue | string[] | undefined>;

/** The chosen option of a select group, or undefined. */
export function selectValue(value: FilterValue, id: string): string | undefined {
  const v = value[id];
  return typeof v === 'string' ? v : undefined;
}

/** The chosen options of a multi group (empty when none). */
export function multiValue(value: FilterValue, id: string): string[] {
  const v = value[id];
  return Array.isArray(v) ? v : [];
}

/** The value of a range group, falling back to its bounds and toggle default. */
export function rangeValue(value: FilterValue, g: RangeFilterGroup): Required<RangeValue> {
  const v = value[g.id];
  const r = v !== undefined && typeof v === 'object' && !Array.isArray(v) ? v : undefined;
  return {
    min: r?.min ?? g.min,
    max: r?.max ?? g.max,
    toggle: r?.toggle ?? g.toggle?.default ?? false,
  };
}

function rangeApplied(g: RangeFilterGroup, v: Required<RangeValue>): boolean {
  return (
    v.min !== g.min || v.max !== g.max || (g.toggle !== undefined && v.toggle !== g.toggle.default)
  );
}

export interface FilterBarLabels {
  /** The Filter button and the sheet / panel title. */
  filters: string;
  /** The button's accessible name when filters are applied, e.g. `Filters, 2 applied`. */
  filtersApplied: (count: number) => string;
  /** Under the search row, clears every applied filter. */
  clearAll: string;
  /** In the panel footer, clears every applied filter. */
  reset: string;
  /** The panel's confirm button, e.g. `Show 12`. */
  show: (count: number) => string;
  /** An applied chip's accessible name, e.g. `Remove Australia`. */
  remove: (label: string) => string;
}

export interface FilterBarProps {
  /** Optional responsive results + RHS filters layout; existing consumers remain toolbar. */
  presentation?: 'toolbar' | 'sidebar';
  children?: ReactNode;
  groups: FilterGroup[];
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  /** Rows matching the current filters; feeds the panel's "Show N". */
  resultCount: number;
  /** Search field (e.g. `SearchInput`), always full width. Reset and chip removal never touch it. */
  search?: ReactNode;
  /** Whether the search has text. With `onClearSearch`, keeps "Clear all" showing for it. */
  searchActive?: boolean;
  /**
   * Clears the caller's search state. "Clear all" calls it alongside the groups reset (the search
   * slot is the caller's node, so only the caller can clear it). Omit it and "Clear all" resets
   * the groups only.
   */
  onClearSearch?: () => void;
  labels: FilterBarLabels;
  /**
   * Narrow container (a master-detail rail): tighter spacing, and a lone dropdown stacks under the
   * search at every viewport width instead of sitting beside it.
   */
  compact?: boolean;
  className?: string;
}

/** A group with at most this many options shows as chips in the panel; longer is a dropdown. */
const MAX_CHIP_OPTIONS = 6;

function FilterIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <path d="M4 7h16M7 12h10M10 17h4" />
    </svg>
  );
}

const linkButton =
  "text-muted focus-visible:ring-ring relative shrink-0 font-mono text-[0.6875rem] font-semibold tracking-[0.08em] uppercase underline before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-[''] focus-visible:ring-2 focus-visible:outline-none";

/** Range editor inside the panel: slider + number inputs (+ toggle); commits a draft after a pause. */
function RangeControl({
  group,
  value,
  onCommit,
  resets,
}: {
  group: RangeFilterGroup;
  value: Required<RangeValue>;
  onCommit: (next: Required<RangeValue>) => void;
  resets: Map<string, () => void>;
}) {
  const [draft, setDraft] = useState<[number, number]>([value.min, value.max]);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Latest slider or typed draft awaiting commit; null = nothing pending.
  const pending = useRef<[number, number] | null>(null);
  const lastCommit = useRef<
    (Required<RangeValue> & { lowerBound: number; upperBound: number }) | null
  >(null);
  // Acknowledging our own commit must not discard a newer draft queued during its input blur.
  // External changes supersede the draft; unrelated filter edits keep its debounce running.
  useEffect(() => {
    const own = lastCommit.current;
    lastCommit.current = null;
    if (
      own &&
      own.min === value.min &&
      own.max === value.max &&
      own.toggle === value.toggle &&
      own.lowerBound === group.min &&
      own.upperBound === group.max
    ) {
      if (!pending.current) setDraft([value.min, value.max]);
      return;
    }
    clearTimeout(timer.current);
    pending.current = null;
    setDraft([value.min, value.max]);
  }, [value.min, value.max, value.toggle, group.min, group.max]);
  // Reset must also discard a draft when the committed props are already at their defaults.
  useEffect(() => {
    resets.set(group.id, () => {
      clearTimeout(timer.current);
      pending.current = null;
      lastCommit.current = null;
      setDraft([group.min, group.max]);
    });
    return () => {
      resets.delete(group.id);
    };
  }, [resets, group.id, group.min, group.max]);

  const commit = (lo: number, hi: number, toggle = value.toggle) => {
    clearTimeout(timer.current);
    pending.current = null;
    const min = Math.min(group.max, Math.max(group.min, Math.min(lo, hi)));
    const max = Math.max(group.min, Math.min(group.max, Math.max(lo, hi)));
    setDraft([min, max]);
    if (min === value.min && max === value.max && toggle === value.toggle) return;
    lastCommit.current = { min, max, toggle, lowerBound: group.min, upperBound: group.max };
    onCommit({ min, max, toggle });
  };
  const latestCommit = useRef(commit);
  useLayoutEffect(() => {
    latestCommit.current = commit;
  });
  const slide = (next: [number, number]) => {
    setDraft(next);
    pending.current = next;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => latestCommit.current(next[0], next[1]), 250);
  };
  // Closing or resizing the editor preserves a pending slider or unblurred input draft.
  const flush = useRef<() => void>(() => {});
  flush.current = () => {
    clearTimeout(timer.current);
    const p = pending.current;
    if (p) commit(p[0], p[1]);
  };
  useEffect(() => () => flush.current(), []);
  const typed = (which: 0 | 1, raw: string) => {
    const n = raw === '' ? (which === 0 ? group.min : group.max) : Number(raw);
    if (Number.isNaN(n)) return;
    const next: [number, number] = which === 0 ? [n, draft[1]] : [draft[0], n];
    clearTimeout(timer.current);
    pending.current = next;
    setDraft(next);
  };
  const input = (which: 0 | 1, label: string) => (
    <Input
      type="number"
      inputMode="numeric"
      aria-label={label}
      min={group.min}
      max={group.max}
      step={group.step}
      value={draft[which]}
      onChange={(e) => typed(which, e.target.value)}
      onBlur={() => commit(draft[0], draft[1])}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(draft[0], draft[1]);
      }}
      className="h-11 text-base"
    />
  );

  return (
    <div className="flex flex-col gap-3">
      <RangeSlider
        min={group.min}
        max={group.max}
        step={group.step}
        value={draft}
        onChange={slide}
        showTrackLabels
        minLabel={group.minLabel}
        maxLabel={group.maxLabel}
      />
      <div className="grid grid-cols-2 gap-3">
        {input(0, group.minLabel)}
        {input(1, group.maxLabel)}
      </div>
      {group.toggle && (
        <label className="text-foreground flex min-h-11 items-center gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={value.toggle}
            onChange={(e) => commit(draft[0], draft[1], e.target.checked)}
            className="border-border accent-primary h-4 w-4 rounded"
          />
          <span>{group.toggle.label}</span>
        </label>
      )}
    </div>
  );
}

interface AppliedFilter {
  key: string;
  adornment?: ReactNode;
  text: string;
  remove: () => void;
}

/**
 * THE filter control for every list (hand-rolled filter rows are not added).
 *
 * - One select group: that group is a dropdown beside the search box (wraps under it, full width,
 *   on narrow screens; always under it with `compact`). No button, no panel, no chip row; "Clear all"
 *   follows the dropdown on the same row while it is off "All" or the search has text.
 * - No shown group: the search alone, with the same "Clear all" for search text.
 * - Two or more groups, or any range or multi group: a "Filter" button with the applied count beside the
 *   search. From `lg` it opens a popover anchored to the button, below `lg` the bottom sheet.
 *   Select groups of up to six options are chips, longer ones dropdowns; a multi group is always
 *   chips (each toggles); a range is a slider with min/max inputs. Applied filters show under the search as removable chips plus "Clear all" (which also clears the search via `onClearSearch`, and shows
 *   for search text alone); the
 *   panel footer has "Reset" and "Show N".
 *
 * Filters apply live. Select and multi groups with fewer than two options are not rendered. Option and group
 * labels keep the caller's casing; every string comes from `labels` / the groups. Option
 * `adornment`s show on panel chips and applied chips only (native dropdown options cannot).
 */
export function FilterBar({
  groups,
  value,
  onChange,
  resultCount,
  search,
  searchActive = false,
  onClearSearch,
  labels,
  compact = false,
  presentation = 'toolbar',
  children,
  className,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const [rangeResets] = useState(() => new Map<string, () => void>());
  // Multiple range controls can flush in the same unmount, before a parent render merges them.
  const latestValue = useRef(value);
  useLayoutEffect(() => {
    latestValue.current = value;
  }, [value]);
  const change = (next: FilterValue) => {
    latestValue.current = next;
    onChange(next);
  };
  const panelId = useId();
  const sidebar = presentation === 'sidebar';
  const desktop = useIsDesktop(sidebar ? 'xl' : 'lg');
  const shown = groups.filter((g) =>
    g.kind === 'range'
      ? g.min < g.max
      : g.options.length >= 2 ||
        (g.kind === 'select' && g.showWhenSingle === true && g.options.length === 1),
  );
  const clearOne = (g: FilterGroup) => {
    rangeResets.get(g.id)?.();
    change({ ...latestValue.current, [g.id]: undefined });
  };
  // A valid selection can still exclude unassigned rows after its group becomes trivial.
  // Sidebar chips remain removable even when the group's editor is omitted.
  const applied = (sidebar ? groups : shown).flatMap<AppliedFilter>((g) => {
    if (g.kind === 'range') {
      const v = rangeValue(value, g);
      if (!rangeApplied(g, v)) return [];
      // The chip says only what changed: the range, the toggle, or both.
      const rangeMoved = v.min !== g.min || v.max !== g.max;
      const toggleMoved = g.toggle !== undefined && v.toggle !== g.toggle.default;
      const rangeText = g.formatChip ? g.formatChip(v) : `${g.label} ${v.min}\u2013${v.max}`;
      const toggleAlone = toggleMoved ? g.toggle?.chipLabel?.(v.toggle) : undefined;
      const toggleSuffix = toggleMoved
        ? (g.toggle?.chipSuffix ?? g.toggle?.chipLabel)?.(v.toggle)
        : undefined;
      const text = !rangeMoved
        ? (toggleAlone ?? rangeText)
        : toggleSuffix
          ? `${rangeText} \u00b7 ${toggleSuffix}`
          : rangeText;
      return [{ key: g.id, text, remove: () => clearOne(g) }];
    }
    if (g.kind === 'multi') {
      const chosen = multiValue(value, g.id);
      return g.options
        .filter((o) => chosen.includes(o.value))
        .map((o) => ({
          key: `${g.id}:${o.value}`,
          text: o.label,
          adornment: o.adornment,
          remove: () => toggleMulti(g, o.value),
        }));
    }
    const chosen = g.options.find((o) => o.value === selectValue(value, g.id));
    return chosen
      ? [{ key: g.id, text: chosen.label, adornment: chosen.adornment, remove: () => clearOne(g) }]
      : [];
  });
  const count = applied.length;

  const pick = (g: SelectFilterGroup, optionValue: string | undefined) =>
    change({
      ...latestValue.current,
      [g.id]:
        optionValue === undefined || selectValue(value, g.id) === optionValue
          ? undefined
          : optionValue,
    });
  const setRange = (g: RangeFilterGroup, next: Required<RangeValue>) =>
    change({ ...latestValue.current, [g.id]: rangeApplied(g, next) ? next : undefined });
  function toggleMulti(g: MultiFilterGroup, optionValue: string) {
    const chosen = multiValue(value, g.id);
    const next = chosen.includes(optionValue)
      ? chosen.filter((v) => v !== optionValue)
      : g.options.map((o) => o.value).filter((v) => v === optionValue || chosen.includes(v));
    change({ ...latestValue.current, [g.id]: next.length > 0 ? next : undefined });
  }
  const discardRanges = () => rangeResets.forEach((reset) => reset());
  const resetGroups = () => {
    discardRanges();
    change(Object.fromEntries(groups.map((g) => [g.id, undefined])) as FilterValue);
  };
  // "Clear all" also empties the search; the panel's Reset stays groups-only.
  const clearAll = () => {
    if (count > 0 || sidebar) resetGroups();
    else discardRanges();
    onClearSearch?.();
  };
  const showClearAll = count > 0 || (searchActive && !!onClearSearch);

  const dropdown = (g: SelectFilterGroup, className?: string) => (
    <Select
      aria-label={g.label}
      value={selectValue(value, g.id) ?? ''}
      onChange={(e) => change({ ...latestValue.current, [g.id]: e.target.value || undefined })}
      className={cn('h-11 text-base', className)}
    >
      <option value="">
        {g.allLabel}
        {g.allCount !== undefined ? ` (${g.allCount})` : ''}
      </option>
      {g.options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
          {o.count !== undefined ? ` (${o.count})` : ''}
        </option>
      ))}
    </Select>
  );

  // Every layout renders root > row > search slot, so the search keeps its place in the tree (and
  // its focus and typed text) when options arrive late and the layout flips.
  const searchSlot = (slotClassName: string) =>
    search ? <div className={slotClassName}>{search}</div> : null;

  // Outside the panel layout there is no chip row, so "Clear all" sits on the search/dropdown row.
  const inlineClearAll = showClearAll ? (
    <button type="button" onClick={clearAll} className={cn(linkButton, 'self-center')}>
      {labels.clearAll}
    </button>
  ) : null;

  if (shown.length === 0 && !sidebar) {
    return search ? (
      <div className={cn('min-w-0', className)}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {searchSlot('min-w-0 flex-1')}
          {inlineClearAll}
        </div>
      </div>
    ) : null;
  }

  const only = shown.length === 1 ? shown[0] : undefined;
  if (!sidebar && only && only.kind === 'select') {
    return (
      <div className={className}>
        <div
          data-filter-layout="select"
          className={cn('flex items-start', compact ? 'flex-col gap-1.5' : 'flex-wrap gap-2')}
        >
          {searchSlot(cn('min-w-0', compact ? 'w-full' : 'flex-[1_1_16rem]'))}
          <div
            className={cn(
              'min-w-0',
              compact ? 'w-full' : 'flex-[1_1_10rem] sm:flex-none sm:basis-60',
            )}
          >
            {dropdown(only)}
          </div>
          {inlineClearAll}
        </div>
      </div>
    );
  }

  const wrappingChip = sidebar ? 'min-w-0 max-w-full whitespace-normal text-left' : undefined;
  const optionLabel = (option: FilterOption) => (
    <>
      {option.adornment}
      <span className={sidebar ? 'min-w-0 [overflow-wrap:anywhere]' : undefined}>
        {option.label}
      </span>
      {option.count !== undefined && <Numeric className="shrink-0">{option.count}</Numeric>}
    </>
  );
  const panelBody = (
    <div className="flex flex-col gap-4">
      {shown.map((g) => (
        <div key={g.id} role="group" aria-label={g.label} className="flex flex-col gap-1.5">
          <p
            className={
              sidebar
                ? 'text-muted font-mono text-[0.6875rem] font-semibold tracking-wide uppercase'
                : 'text-muted text-xs font-semibold'
            }
          >
            {g.label}
          </p>
          {g.kind === 'range' ? (
            <RangeControl
              group={g}
              value={rangeValue(value, g)}
              onCommit={(next) => setRange(g, next)}
              resets={rangeResets}
            />
          ) : g.kind === 'multi' ? (
            <div className={cn('flex flex-wrap gap-x-1.5', sidebar && 'gap-y-3')}>
              {g.options.map((o) => (
                <Chip
                  key={o.value}
                  preserveCase
                  className={wrappingChip}
                  selected={multiValue(value, g.id).includes(o.value)}
                  onClick={() => toggleMulti(g, o.value)}
                >
                  {optionLabel(o)}
                </Chip>
              ))}
            </div>
          ) : sidebar || g.options.length <= MAX_CHIP_OPTIONS ? (
            <div className={cn('flex flex-wrap gap-x-1.5', sidebar && 'gap-y-3')}>
              {sidebar && (
                <Chip
                  preserveCase
                  className={wrappingChip}
                  selected={selectValue(value, g.id) === undefined}
                  onClick={() => pick(g, undefined)}
                >
                  <span className="min-w-0 [overflow-wrap:anywhere]">{g.allLabel}</span>
                  {g.allCount !== undefined && <Numeric className="shrink-0">{g.allCount}</Numeric>}
                </Chip>
              )}
              {g.options.map((o) => (
                <Chip
                  key={o.value}
                  preserveCase
                  className={wrappingChip}
                  selected={selectValue(value, g.id) === o.value}
                  onClick={() => pick(g, o.value)}
                >
                  {optionLabel(o)}
                </Chip>
              ))}
            </div>
          ) : (
            dropdown(g)
          )}
        </div>
      ))}
    </div>
  );
  const footer = (
    <>
      <Button variant="outline" onClick={resetGroups}>
        {labels.reset}
      </Button>
      <Button onClick={() => setOpen(false)}>{labels.show(resultCount)}</Button>
    </>
  );

  const trigger = (
    <Chip
      icon={<FilterIcon />}
      count={count}
      highlight={count > 0}
      aria-label={count > 0 ? labels.filtersApplied(count) : labels.filters}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      onClick={desktop ? undefined : () => setOpen(true)}
    >
      {labels.filters}
    </Chip>
  );

  if (sidebar) {
    return (
      <div
        data-filter-layout="sidebar"
        className={cn(
          'min-w-0 space-y-3 xl:space-y-0 print:block',
          shown.length > 0 &&
            'xl:grid xl:grid-cols-[minmax(0,1fr)_16rem] xl:grid-rows-[max-content_1fr] xl:items-start xl:gap-x-6 xl:gap-y-3',
          className,
        )}
      >
        <div className="contents">
          <div
            className={cn(
              'space-y-2 print:hidden',
              shown.length > 0 && 'xl:col-start-2 xl:row-start-1',
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              {searchSlot('min-w-0 flex-1')}
              {!desktop && shown.length > 0 && trigger}
            </div>
            {showClearAll && (
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-3">
                {applied.map(({ key, text, adornment, remove }) => (
                  <Chip
                    key={key}
                    preserveCase
                    className={wrappingChip}
                    removeLabel={labels.remove(text)}
                    onRemove={remove}
                  >
                    {adornment}
                    <span className="min-w-0 [overflow-wrap:anywhere]">{text}</span>
                  </Chip>
                ))}
                <button type="button" onClick={clearAll} className={cn(linkButton, 'min-h-11')}>
                  {labels.clearAll}
                </button>
              </div>
            )}
          </div>
          <div
            className={cn(
              'mt-3 min-w-0 xl:mt-0',
              shown.length > 0 && 'xl:col-start-1 xl:row-span-2 xl:row-start-1',
            )}
          >
            {children}
          </div>
        </div>
        {desktop && shown.length > 0 && (
          <aside
            aria-label={labels.filters}
            className="sticky top-4 max-h-[calc(100dvh-2rem)] min-w-0 overflow-y-auto xl:col-start-2 xl:row-start-2 print:hidden"
          >
            <Card className="min-w-0 space-y-4 border-0 bg-transparent p-1 shadow-none">
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-sm font-semibold">{labels.filters}</h2>
                <button type="button" onClick={resetGroups} className={cn(linkButton, 'min-h-11')}>
                  {labels.reset}
                </button>
              </div>
              {panelBody}
            </Card>
          </aside>
        )}
        {!desktop && shown.length > 0 && (
          <Dialog open={open} onOpenChange={setOpen} placement="bottom">
            <DialogContent className="max-h-[85vh] overflow-y-auto rounded-b-none">
              <div id={panelId}>
                <DialogHeader>
                  <DialogTitle>{labels.filters}</DialogTitle>
                </DialogHeader>
                <div className="p-6">{panelBody}</div>
                <DialogFooter>{footer}</DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  return (
    <div
      data-filter-layout="panel"
      className={cn('flex flex-col', compact ? 'gap-1.5' : 'gap-2', className)}
    >
      <div className={cn('flex items-center', compact ? 'gap-1.5' : 'gap-2')}>
        {searchSlot('min-w-0 flex-1')}
        {desktop ? (
          <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
            <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                id={panelId}
                align={compact ? 'start' : 'end'}
                sideOffset={6}
                collisionPadding={16}
                aria-label={labels.filters}
                className="border-border bg-card text-foreground z-50 max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border p-4 shadow-md"
              >
                {panelBody}
                <div className="mt-4 flex justify-end gap-2">{footer}</div>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        ) : (
          trigger
        )}
      </div>

      {showClearAll && (
        <div className="flex flex-wrap items-center gap-x-1.5">
          {applied.map(({ key, text, adornment, remove }) => (
            <Chip key={key} preserveCase removeLabel={labels.remove(text)} onRemove={remove}>
              {adornment}
              {text}
            </Chip>
          ))}
          <button type="button" onClick={clearAll} className={linkButton}>
            {labels.clearAll}
          </button>
        </div>
      )}

      {!desktop && (
        <Dialog open={open} onOpenChange={setOpen} placement="bottom">
          <DialogContent className="max-h-[85vh] overflow-y-auto rounded-b-none">
            <div id={panelId}>
              <DialogHeader>
                <DialogTitle>{labels.filters}</DialogTitle>
              </DialogHeader>
              <div className="p-6">{panelBody}</div>
              <DialogFooter>{footer}</DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
